import React, { useState, useEffect, useCallback } from "react";
import { Users, UserCheck, Edit3, Save, RefreshCw, Copy, Check, AlertCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { getAllMeetingRoles, bulkUpdateMeetingRoles } from "shared/api/function_data";
import { invalidateConfig } from "shared/lib/queryClient";
import { CollapsibleSection, FormField } from "./AdminShared";
import { CURRENT_WBR_WEEK } from "./AdminConstants";

const ROLES = [
  { key: "meeting_coordination", label: "Coordinator", icon: Users },
  { key: "meeting_facilitator", label: "Facilitator", icon: UserCheck },
  { key: "meeting_note_taker", label: "Note Taker", icon: Edit3 },
];

function MeetingRolesTab({ config, setConfig, functionId, year }) {
  const queryClient = useQueryClient();
  // ─── Current week editing (existing behavior for main Save Config button) ───
  const details = config.function_details;
  const updateRole = (role, value) => {
    setConfig((prev) => ({
      ...prev,
      function_details: {
        ...prev.function_details,
        [role]: { ...prev.function_details[role], login: value },
      },
    }));
  };

  // ─── Multi-week roster state ───
  const [weekRoles, setWeekRoles] = useState({});
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [rosterError, setRosterError] = useState(null);
  const [editingWeek, setEditingWeek] = useState(null);
  const [editBuffer, setEditBuffer] = useState({});
  const [savingWeek, setSavingWeek] = useState(null);
  const [savedWeeks, setSavedWeeks] = useState(new Set());

  // ─── Fetch all weeks' roles in ONE API call ───
  const fetchRoster = useCallback(async () => {
    if (!functionId || !year) return;
    setLoadingRoster(true);
    setRosterError(null);
    try {
      const roles = await getAllMeetingRoles(functionId, year);
      setWeekRoles(roles);
    } catch (err) {
      setRosterError(err.message || "Failed to load meeting roster");
      setWeekRoles({});
    } finally {
      setLoadingRoster(false);
    }
  }, [functionId, year]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  // ─── Inline edit ───
  const startEdit = (weekNum) => {
    setEditingWeek(weekNum);
    const weekKey = String(weekNum);
    setEditBuffer(JSON.parse(JSON.stringify(weekRoles[weekKey] || {
      meeting_coordination: { login: "" },
      meeting_facilitator: { login: "" },
      meeting_note_taker: { login: "" },
    })));
  };

  const cancelEdit = () => {
    setEditingWeek(null);
    setEditBuffer({});
  };

  // Save single week via bulk endpoint (1 API call)
  const saveWeekRoles = async (weekNum) => {
    setSavingWeek(weekNum);
    try {
      await bulkUpdateMeetingRoles(functionId, year, {
        [String(weekNum)]: editBuffer,
      });
      // Meeting roles feed the cover page config — refresh it so the change shows.
      invalidateConfig(queryClient, functionId);
      setWeekRoles((prev) => ({ ...prev, [String(weekNum)]: { ...editBuffer } }));
      setSavedWeeks((prev) => new Set([...prev, weekNum]));
      setEditingWeek(null);
      setEditBuffer({});
      setTimeout(() => setSavedWeeks((prev) => { const next = new Set(prev); next.delete(weekNum); return next; }), 2000);
    } catch (err) {
      setRosterError(`Failed to save Week ${weekNum}: ${err.message}`);
    } finally {
      setSavingWeek(null);
    }
  };

  // ─── Copy roles from one week to a range (1 API call) ───
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyTarget, setCopyTarget] = useState({ start: 1, end: 52 });
  const [copying, setCopying] = useState(false);

  const openCopyModal = (weekNum) => {
    setCopySource(weekNum);
    setCopyTarget({ start: weekNum + 1, end: Math.min(weekNum + 4, 52) });
    setShowCopyModal(true);
  };

  const executeCopy = async () => {
    if (!copySource) return;
    const sourceKey = String(copySource);
    const sourceRoles = weekRoles[sourceKey] || {
      meeting_coordination: { login: "" },
      meeting_facilitator: { login: "" },
      meeting_note_taker: { login: "" },
    };

    setCopying(true);
    try {
      // Build bulk update payload — single API call for all target weeks
      const updates = {};
      for (let w = copyTarget.start; w <= copyTarget.end; w++) {
        updates[String(w)] = JSON.parse(JSON.stringify(sourceRoles));
      }
      await bulkUpdateMeetingRoles(functionId, year, updates);
      invalidateConfig(queryClient, functionId);

      // Update local state
      setWeekRoles((prev) => {
        const next = { ...prev };
        for (let w = copyTarget.start; w <= copyTarget.end; w++) {
          next[String(w)] = JSON.parse(JSON.stringify(sourceRoles));
        }
        return next;
      });

      setShowCopyModal(false);
      const newSaved = new Set();
      for (let w = copyTarget.start; w <= copyTarget.end; w++) newSaved.add(w);
      setSavedWeeks(newSaved);
      setTimeout(() => setSavedWeeks(new Set()), 3000);
    } catch (err) {
      setRosterError(`Copy failed: ${err.message}`);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="admin-tab-content">
      {/* ─── Current Week Quick Edit ─── */}
      <CollapsibleSection
        title={`Current Week Roles (Week ${details.week || "?"})`}
        subtitle="Edit roles for the currently viewed week — saves with the main Save Config button"
        defaultOpen={false}
      >
        <div className="admin-roles-grid">
          {ROLES.map(({ key, label, icon: Icon }) => (
            <div className="admin-role-card" key={key}>
              <div className="admin-role-card__header">
                <div className="admin-role-card__icon"><Icon size={20} /></div>
                <h4 className="admin-role-card__title">{label}</h4>
              </div>
              <FormField label="Login / Alias" required>
                <input className="admin-input" value={details[key]?.login || ""} onChange={(e) => updateRole(key, e.target.value)} placeholder="Enter user login" />
              </FormField>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* ─── Full Year Roster (1 API call to load, 1 call per save) ─── */}
      <CollapsibleSection
        title="Full Year Meeting Roster"
        subtitle={`All weeks for ${year} — 1 API call to load, edit inline, copy across weeks`}
        defaultOpen={true}
        actions={
          <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={fetchRoster} disabled={loadingRoster}>
            <RefreshCw size={12} className={loadingRoster ? "admin-spin" : ""} /> Refresh
          </button>
        }
      >
        {rosterError && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", background: "#fef2f2", borderRadius: "8px", marginBottom: "12px", fontSize: "13px", color: "#991b1b" }}>
            <AlertCircle size={14} />
            <span>{rosterError}</span>
            <button onClick={() => setRosterError(null)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#991b1b" }}>✕</button>
          </div>
        )}

        {loadingRoster ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0", gap: "12px", color: "#6b7280" }}>
            <div className="admin-loading__spinner" style={{ width: "20px", height: "20px" }} />
            <span>Loading roster (single API call)...</span>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>Week</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Coordinator</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Facilitator</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, color: "#374151" }}>Note Taker</th>
                  <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: 600, color: "#374151", width: "130px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 52 }, (_, i) => i + 1).map((weekNum) => {
                  const weekKey = String(weekNum);
                  const roles = weekRoles[weekKey];
                  const isEditing = editingWeek === weekNum;
                  const isSaving = savingWeek === weekNum;
                  const isSaved = savedWeeks.has(weekNum);
                  const isCurrent = weekNum === CURRENT_WBR_WEEK;

                  return (
                    <tr key={weekNum} style={{
                      borderBottom: "1px solid #f3f4f6",
                      background: isEditing ? "#eff6ff" : isCurrent ? "#f0fdf4" : isSaved ? "#ecfdf5" : "transparent",
                      transition: "background 0.2s",
                    }}>
                      <td style={{ padding: "8px 12px", fontWeight: 600, whiteSpace: "nowrap", color: isCurrent ? "#16a34a" : "#374151" }}>
                        W{weekNum}
                        {isCurrent && <span style={{ fontSize: "10px", marginLeft: "4px", color: "#16a34a", fontWeight: 400 }}>(now)</span>}
                      </td>

                      {isEditing ? (
                        <>
                          {ROLES.map(({ key }) => (
                            <td key={key} style={{ padding: "6px 8px" }}>
                              <input
                                style={{ width: "100%", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px", outline: "none" }}
                                value={editBuffer[key]?.login || ""}
                                onChange={(e) => setEditBuffer((prev) => ({ ...prev, [key]: { ...prev[key], login: e.target.value } }))}
                                placeholder="login"
                              />
                            </td>
                          ))}
                          <td style={{ padding: "6px 8px", textAlign: "center" }}>
                            <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                              <button onClick={() => saveWeekRoles(weekNum)} disabled={isSaving}
                                style={{ padding: "5px 10px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                                {isSaving ? "..." : <><Save size={11} /> Save</>}
                              </button>
                              <button onClick={cancelEdit}
                                style={{ padding: "5px 10px", background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                                ✕
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          {ROLES.map(({ key }) => (
                            <td key={key} style={{ padding: "8px 12px", color: roles?.[key]?.login ? "#374151" : "#9ca3af" }}>
                              {roles?.[key]?.login || "—"}
                            </td>
                          ))}
                          <td style={{ padding: "8px 12px", textAlign: "center" }}>
                            <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                              {isSaved ? (
                                <span style={{ color: "#16a34a", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}>
                                  <Check size={12} /> Saved
                                </span>
                              ) : (
                                <>
                                  <button onClick={() => startEdit(weekNum)} title="Edit"
                                    style={{ padding: "4px 8px", background: "transparent", border: "1px solid #e5e7eb", borderRadius: "5px", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px", fontSize: "12px", color: "#4b5563" }}>
                                    <Edit3 size={11} /> Edit
                                  </button>
                                  <button onClick={() => openCopyModal(weekNum)} title="Copy to other weeks"
                                    style={{ padding: "4px 8px", background: "transparent", border: "1px solid #e5e7eb", borderRadius: "5px", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px", fontSize: "12px", color: "#4b5563" }}>
                                    <Copy size={11} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleSection>

      {/* ─── Copy Modal ─── */}
      {showCopyModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => setShowCopyModal(false)}>
          <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "400px", maxWidth: "90vw", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 8px", fontSize: "16px", fontWeight: 600 }}>Copy Roles from Week {copySource}</h3>
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#6b7280" }}>
              Copy coordinator, facilitator, and note taker to a range of weeks in a single API call.
            </p>
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "12px", fontWeight: 500, color: "#374151", display: "block", marginBottom: "4px" }}>From Week</label>
                <select style={{ width: "100%", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  value={copyTarget.start} onChange={(e) => setCopyTarget((prev) => ({ ...prev, start: Number(e.target.value) }))}>
                  {Array.from({ length: 52 }, (_, i) => i + 1).map((w) => <option key={w} value={w}>Week {w}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "12px", fontWeight: 500, color: "#374151", display: "block", marginBottom: "4px" }}>To Week</label>
                <select style={{ width: "100%", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  value={copyTarget.end} onChange={(e) => setCopyTarget((prev) => ({ ...prev, end: Number(e.target.value) }))}>
                  {Array.from({ length: 52 }, (_, i) => i + 1).filter((w) => w >= copyTarget.start).map((w) => <option key={w} value={w}>Week {w}</option>)}
                </select>
              </div>
            </div>
            <p style={{ fontSize: "12px", color: "#92400e", background: "#fffbeb", padding: "8px 12px", borderRadius: "6px", margin: "0 0 16px" }}>
              ⚠️ This will overwrite roles in weeks {copyTarget.start}–{copyTarget.end} (single API call)
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowCopyModal(false)}
                style={{ padding: "8px 16px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}>Cancel</button>
              <button onClick={executeCopy} disabled={copying}
                style={{ padding: "8px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                {copying ? "Copying..." : <><Copy size={12} /> Copy Roles</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MeetingRolesTab;
